import React from "react";
import Cookies from "js-cookie";
import Avatar from "./Avatar";
import classes from "./userProfileList.module.scss";
import { postReq } from "../utils/request";
import { setJwtToken } from "../utils/token";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { useNavigate } from "react-router-dom";
import useCustomNavigate from "../hooks/useCustomNavigate";
import { openBugReport } from "../components/BugReportButton";

interface UserProfileListProps {
    firstName: string;
    lastName?: string;
    photoFile?: string;
    jobPosition: string;
    onClose: () => void;
}

/** Account menu under the header avatar: who you are, your pages, sign out. */
const UserProfileList: React.FC<UserProfileListProps> = ({ firstName, lastName, photoFile, jobPosition, onClose }) => {
    const navigate = useNavigate();
    const customNavigate = useCustomNavigate();
    const { setCurrentUser } = useUserPermissions();

    const handleLogout = async () => {
        try {
            // Invalidate the session server-side first.
            const response = await postReq("users/logout", {});

            if (response.status === 200) {
                setJwtToken("");
                Cookies.remove("refreshToken");
                setCurrentUser(null);

                // Tell every other tab to log out too.
                const logoutChannel = new BroadcastChannel("logout-channel");
                logoutChannel.postMessage({ type: "logout" });
                navigate("/login");
            } else {
                console.error("Logout failed:", response.body.message);
            }
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    const goTo = (to: string) => {
        onClose();
        customNavigate(to);
    };

    const handleReportBug = () => {
        onClose();
        openBugReport();
    };

    return (
        <div className={classes.menu} role="menu" aria-label="Account">
            <div className={classes.identity}>
                <Avatar firstName={firstName} lastName={lastName} photoFile={photoFile} size={40} />
                <span className={classes.identityText}>
                    <span className={classes.name}>{firstName} {lastName}</span>
                    {jobPosition && <span className={classes.job}>{jobPosition}</span>}
                </span>
            </div>
            <div className={classes.separator} />
            <button type="button" role="menuitem" className={`btn-unstyled ${classes.item}`} onClick={() => goTo("/my-profile")}>
                <i className="bi bi-person" aria-hidden="true" />
                <span className={classes.itemLabel}>My profile</span>
            </button>
            <button type="button" role="menuitem" className={`btn-unstyled ${classes.item}`} onClick={() => goTo("/my-calendar")}>
                <i className="bi bi-calendar3" aria-hidden="true" />
                <span className={classes.itemLabel}>My calendar</span>
            </button>
            <button type="button" role="menuitem" className={`btn-unstyled ${classes.item}`} onClick={handleReportBug}>
                <i className="bi bi-bug" aria-hidden="true" />
                <span className={classes.itemLabel}>Report a bug</span>
            </button>
            <div className={classes.separator} />
            <button type="button" role="menuitem" className={`btn-unstyled ${classes.item}`} onClick={handleLogout}>
                <i className="bi bi-box-arrow-right" aria-hidden="true" />
                <span className={classes.itemLabel}>Sign out</span>
            </button>
        </div>
    );
};

export default UserProfileList;
